// Prequalification funnel — shared building blocks used by the buyer and
// seller pages, styled on the §4 public-funnel design system (paper/cream/
// ink palette, serif figures, gold accents): pill choice chips with roving
// keyboard focus, live-formatted currency/phone inputs, the question card,
// the gold progress bar + step pills, the right-rail cards (indicative
// range with count-up, program-at-a-glance, AI guide), and the result
// ceremony (hero seal / document cards / finalize form / SoftDecline
// interstitial / success screen).

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  FormField,
  Input,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { formatCompactCurrency, formatCurrency, formatDateInTz } from "@/design-system/lib/format";
import { cn } from "@/design-system/lib/utils";
import {
  prequalificationApi,
  type PrequalDocumentLink,
  type PrequalEngineResult,
  type PrequalFinalizeRequest,
  type PrequalFinalizeResult,
  type PrequalRole,
} from "../../api/prequalification";
import { loadAttribution } from "./prequalAttribution";
import {
  belowProgramMinimumOnSchedule,
  DOWN_PAYMENT_SECOND_PERSON,
  FIRST_TIMER_CAP_NOTE,
  FUNNEL_QUESTION_DEFAULTS,
  PROGRAM_RATE_AND_STRUCTURE,
  PROGRAM_SIZING_RULE,
  programMinimumBandNote,
  SIZING_METER_PENDING_NOTE,
  SIZING_METER_SCHEDULE_NOTE,
  UNSURE_NOTE,
  type ChoiceOption,
  type FunnelQuestionKey,
  type KnockoutQuestionDef,
} from "./prequalContent";
// The program floor, read from the terms the sizing itself clamps against
// rather than typed here: a literal minimum in this file is a second source of
// truth for the number the band is being compared to.
import { PREQUAL_PROGRAM_TERMS } from "./prequalSizing";
// The funnel's consent + fine print, server-published with the compiled-in
// constants as the fallback. Always populated, so no consumer here guards a null.
import { useFunnelCopy } from "./PrequalBrandContext";
import { hasText, textOrUndefined } from "./blankCopy";

// ── shared style fragments ──────────────────────────────────────────────

const CARD_SHELL =
  "rounded-[14px] border border-line bg-white-pure shadow-[0_10px_34px_rgba(20,20,20,0.07)]";
// Mobile: the 0.24em overline tracking blows a 36-character program name
// past a 327px phone column — tighten to the `widest` token below sm.
const OVERLINE = "text-label uppercase tracking-widest sm:tracking-[0.24em] text-gold-deep";
const OVERLINE_MUTED = "text-label uppercase tracking-widest sm:tracking-[0.24em] text-ink-mid";
const GOLD_GRADIENT = "bg-gradient-to-r from-gold-deep via-gold to-gold-highlight";

/** §3.3 — the one message shown for transport-level finalize failures. */
export const FRIENDLY_FINALIZE_ERROR =
  "We’re having trouble right now — please try again in a moment.";

// ── money / phone parsing + live formatting ─────────────────────────────

/** Lenient "$1,250,000" / "1250000" → number; null when unparseable. */
export function parseMoney(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * A money answer normalised so that "nothing" has ONE representation.
 *
 * 🔴 Use this ONLY where zero cannot be a real answer. Some money fields on
 * these funnels are legitimately zero — a seller wanting no cash at closing
 * states a down payment of 0 and means it — and routing one of those through
 * here would silently discard a real answer. The distinction is per-field and
 * the call site is where it gets decided.
 *
 * It exists because `parseMoney` is deliberately lenient: it returns 0 for "0"
 * and for "$0", so a typed zero and a blank field arrive as different values
 * from a user who meant the same thing. Every downstream reader then has to
 * re-decide what that 0 meant, and they do not reliably agree.
 */
export function nullIfNotPositive(value: number | null): number | null {
  return value != null && value > 0 ? value : null;
}

/** Live thousands separators as the user types: strip non-digits, group. */
export function formatMoneyTyping(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  return Number(digits).toLocaleString("en-US");
}

/**
 * Bare digits for storage — the country code absorbed, THEN capped at ten.
 *
 * 🔴 The order is the whole point. A leading `1` is always the US country
 * code and never an area code: the NANP assigns area codes `[2-9]XX`, so no
 * real US number begins with one. Capping first and stripping never took
 * `1 (480) 555-0142` (eleven digits) down to `1480555014` — it dropped the
 * LAST digit and shifted every other one place left, which renders as the
 * entirely plausible `(148) 055-5014` with no inline error for the buyer to
 * notice. libphonenumber then correctly rejects `148` as a region-US area
 * code, `BuyerLeadService.ValidateContact` throws, and
 * `PrequalificationIntakeService.TryLinkBuyerCrmAsync` swallows it — so the
 * lead was lost, silently, to a leading `1`. Measured on staging as the cause
 * of PQ-B-2026-0009/-0010/-0011.
 *
 * Stripping unconditionally rather than only at eleven digits is what makes
 * TYPING work as well as pasting: the `1` disappears as it is entered and the
 * mask builds forward correctly, instead of showing a wrong area code that
 * snaps only once the eleventh digit lands.
 */
export function normalizePhoneDigits(raw: string): string {
  const d = raw.replace(/\D/g, "");
  return (d.startsWith("1") ? d.slice(1) : d).slice(0, 10);
}

/** Progressive `(480) 555-0142` mask over a stored digit string. */
export function formatPhoneTyping(digits: string): string {
  const d = normalizePhoneDigits(digits);
  if (!d) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ── motion helpers ──────────────────────────────────────────────────────

function prefersReducedMotion(): boolean {
  return (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function" ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

const COUNT_UP_MS = 700;

/** §2 — the animation-RESTART debounce: rapid keystrokes retarget the
 *  count-up but only the last quiet value animates (one animation, not N). */
export const COUNT_UP_RESTART_DEBOUNCE_MS = 250;

/**
 * ~700ms ease-out count-up over a GROUP of figures that are read together.
 * Reduced motion (or a first appearance) snaps instantly; the restart is
 * debounced ~250ms so mid-typing target changes collapse into ONE animation
 * from the last shown values to the settled targets.
 *
 * The GROUP is the point. One clock, one eased fraction, one state update:
 * every figure is interpolated at the SAME fraction, and interpolating
 * linearly at a shared fraction is monotone in the endpoints — so any ordering
 * that holds for the values we start from and for the values we are heading to
 * also holds at every frame in between (`Math.round` is monotone too, so the
 * rounding cannot break it either). That is what keeps the buyer's headline
 * inside its own estimated band mid-animation: structurally, rather than
 * incidentally. Animating each figure through its own hook could not promise
 * it — each would read its own clock in its own callback and land on its own
 * fraction, which is why the headline used to sit BELOW its band's low end for
 * the best part of a second after every answer change.
 *
 * The group is likewise ALL-OR-NOTHING: if any figure cannot be interpolated
 * (one endpoint absent — appearing, or going away) the whole group snaps.
 * Animating some figures while their neighbours jump is precisely the
 * inconsistency this exists to prevent.
 */
function useCountUpAll(targets: readonly (number | null)[]): readonly (number | null)[] {
  const [display, setDisplay] = useState<readonly (number | null)[]>(targets);
  const shownRef = useRef<readonly (number | null)[]>(targets);
  // Keyed on the target VALUES rather than the array identity: the caller
  // builds a fresh array every render, and an identity-keyed effect would tear
  // down the in-flight animation on each one (React runs the cleanup before it
  // re-runs the effect). Reading `targets` out of the effect's closure is sound
  // for the same reason — the effect only re-runs when the key changes, so the
  // array it closed over always holds exactly the values that key describes.
  const key = targets.map((t) => (t === null ? "~" : t)).join("|");

  useEffect(() => {
    const from = shownRef.current;
    const settle = (values: readonly (number | null)[]) => {
      shownRef.current = values;
      setDisplay(values);
    };
    const pairs = targets.map((to, i) => [from[i] ?? null, to] as const);
    const interpolable =
      from.length === targets.length &&
      pairs.every(([a, b]) => (a === null) === (b === null));
    const moving = interpolable && pairs.some(([a, b]) => a !== null && a !== b);
    if (!moving || prefersReducedMotion()) {
      settle(targets);
      return;
    }
    let raf = 0;
    const timer = window.setTimeout(() => {
      const start = performance.now();
      raf = requestAnimationFrame(function tick(now: number) {
        // Progress is WALL-CLOCK elapsed, never a frame count — so a tab that
        // was hidden (where no frames are served at all) resumes on its first
        // visible frame already at t = 1 and snaps, instead of easing from
        // wherever it was abandoned.
        const t = Math.min(1, (now - start) / COUNT_UP_MS);
        const eased = 1 - Math.pow(1 - t, 3);
        settle(
          pairs.map(([a, b]) => (a === null || b === null ? b : Math.round(a + (b - a) * eased))),
        );
        if (t < 1) raf = requestAnimationFrame(tick);
      });
    }, COUNT_UP_RESTART_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [key]);
  return display;
}

// ── choice chips ────────────────────────────────────────────────────────

interface ChoiceButtonsProps {
  options: ChoiceOption[];
  value: string | null;
  onChange: (value: string) => void;
  /** The question these chips answer — the group's ACCESSIBLE NAME. The chips
   *  carry `aria-pressed`, so without this a screen reader announces their
   *  state perfectly and never says what they are about. It has to live here:
   *  a `<label for>` aimed at a `<div>` is ignored by assistive technology
   *  whether or not the id resolves. */
  groupLabel?: string;
  /** Applied to the group element. `FormField` clones an id onto whatever it
   *  labels, and a component that quietly drops it leaves the `<label for>`
   *  FormField renders pointing at nothing at all. */
  id?: string;
  /** Enter on an already-selected chip advances the step. */
  onAdvance?: () => void;
  /** Optional option value to visually emphasize (subtle gold OUTLINE ring
   *  when NOT selected) — the "expected" answer for a polarity-flipped
   *  question. Deliberately distinct from the solid-gold selected state so an
   *  emphasized-but-untouched question never reads as already answered. */
  emphasize?: string;
  testIdPrefix?: string;
}

/** Pill chips with roving tabindex + arrow-key navigation within the
 *  group; Enter selects (native click), Enter on the selected chip
 *  advances when the parent wires `onAdvance`. */
export function ChoiceButtons({
  options,
  value,
  onChange,
  groupLabel,
  id,
  onAdvance,
  emphasize,
  testIdPrefix,
}: ChoiceButtonsProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIdx = options.findIndex((o) => o.value === value);
  const rovingIdx = selectedIdx >= 0 ? selectedIdx : 0;

  function moveFocus(idx: number) {
    const next = (idx + options.length) % options.length;
    refs.current[next]?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, idx: number) {
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        moveFocus(idx + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        moveFocus(idx - 1);
        break;
      case "Enter":
        if (value === options[idx].value && onAdvance) {
          e.preventDefault();
          onAdvance();
        }
        break;
      default:
        break;
    }
  }

  return (
    <div className="flex flex-wrap gap-space-2" role="group" id={id} aria-label={groupLabel}>
      {options.map((opt, idx) => {
        const selected = value === opt.value;
        const emphasized = !selected && emphasize != null && opt.value === emphasize;
        return (
          <button
            key={opt.value}
            ref={(el) => {
              refs.current[idx] = el;
            }}
            type="button"
            tabIndex={idx === rovingIdx ? 0 : -1}
            aria-pressed={selected}
            onClick={() => onChange(opt.value)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            data-testid={testIdPrefix ? `${testIdPrefix}-${opt.value}` : undefined}
            className={cn(
              // min-h below sm = a 48px touch target (a native button centers
              // its label vertically, so no layout change is needed).
              "rounded-pill border px-space-3 py-space-1 min-h-space-6 sm:min-h-0 text-small transition-colors duration-fast ease-standard",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-1",
              selected
                ? "border-gold bg-gold text-ink-black font-semibold shadow-[inset_0_1px_2px_rgba(20,20,20,0.15)]"
                : emphasized
                  ? "border-gold ring-1 ring-inset ring-gold bg-white-pure text-ink font-medium hover:bg-gold-tint"
                  : "border-line bg-white-pure text-ink hover:border-gold hover:bg-gold-tint",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/** A LABELLED choice group — the visible field label is handed straight to the
 *  group as its accessible name, so the two cannot drift apart.
 *
 *  Use this rather than hand-pairing `FormField` with `ChoiceButtons`: that
 *  pairing renders a `<label for>` aimed at a `<div>`, which names nothing and
 *  (before `ChoiceButtons` accepted an `id`) did not even resolve. */
export function ChoiceField({
  label,
  helperText,
  ...choices
}: { label: string; helperText?: string } & Omit<ChoiceButtonsProps, "groupLabel">) {
  // A blank published label or helper drops its element (D4); the chips stay.
  return (
    <FormField label={textOrUndefined(label)} helperText={textOrUndefined(helperText)}>
      <ChoiceButtons {...choices} groupLabel={textOrUndefined(label)} />
    </FormField>
  );
}

// ── currency + phone inputs ─────────────────────────────────────────────

interface CurrencyFieldProps {
  label: string;
  value: string;
  onChange: (raw: string) => void;
  required?: boolean;
  helperText?: string;
  /** A validation message for THIS field. It renders directly beneath the
   *  input and is wired to it with `aria-describedby`, so the complaint
   *  arrives where the answer is typed rather than at the foot of the page,
   *  and replaces the helper text while it is showing — the same
   *  error-over-helper precedence FormField applies to its own pair. */
  error?: string;
  /** Handed to the underlying `<input>`, so a caller can move focus to it.
   *  The id is minted in HERE (see below), so it is not addressable from
   *  outside and a ref is the only handle on the element. */
  inputRef?: React.Ref<HTMLInputElement>;
  testId?: string;
}

/** Serif digits, a gold `$` inside the field, live thousands separators
 *  as the user types (the stored value is the grouped digit string). */
export function CurrencyField({
  label,
  value,
  onChange,
  required,
  helperText,
  error,
  inputRef,
  testId,
}: CurrencyFieldProps) {
  // The gold `$` overlay means the input sits inside a positioning wrapper, and
  // FormField deliberately refuses to aim a `<label for>` at a `<div>` — so it
  // never reaches the control here and the field would have no accessible name.
  // Minting the id and handing FormField the target explicitly is the escape
  // hatch FormField documents for exactly this shape.
  const id = useId();
  // The SAME wrapper is why the error message is rendered here rather than
  // through FormField's `error` prop: FormField clones `aria-describedby` only
  // onto a child it would point a label at, so the `<div>` swallows the wiring
  // and the message would sit beside an input that never references it.
  // Minting the id and wiring it explicitly is that escape hatch, one attribute
  // along. It renders OUTSIDE the positioning wrapper deliberately — the `$` is
  // vertically centred on that wrapper, so anything added inside it drags the
  // `$` off the input.
  const errorId = `${id}-error`;
  return (
    <FormField
      label={textOrUndefined(label)}
      required={required}
      helperText={error ? undefined : textOrUndefined(helperText)}
      htmlFor={id}
    >
      <div className="relative">
        <span
          className="absolute left-space-3 top-1/2 -translate-y-1/2 font-serif text-gold-deep pointer-events-none"
          aria-hidden="true"
        >
          $
        </span>
        <Input
          id={id}
          ref={inputRef}
          // The visible asterisk is `aria-hidden`, and FormField only clones
          // this onto a control it can reach — which is not the case here.
          aria-required={required || undefined}
          // Input turns this into `aria-invalid` AND the error border, so the
          // field reads as wrong to a screen reader and to an eye alike.
          invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          inputMode="numeric"
          placeholder="0"
          className="pl-7 font-serif"
          value={value}
          onChange={(e) => onChange(formatMoneyTyping(e.target.value))}
          data-testid={testId}
        />
      </div>
      {error && (
        <p id={errorId} className="text-label text-status-error mt-[6px]" role="alert">
          {error}
        </p>
      )}
    </FormField>
  );
}

interface PhoneFieldProps {
  label: string;
  /** Stored as bare digits (max 10); the mask is display-only. */
  value: string;
  onChange: (digits: string) => void;
  helperText?: string;
  testId?: string;
}

/** `(480) 555-0142`-style mask formatted as the user types; digits stored. */
export function PhoneField({ label, value, onChange, helperText, testId }: PhoneFieldProps) {
  // A blank published label or helper drops its element (D4); the input stays.
  return (
    <FormField label={textOrUndefined(label)} helperText={textOrUndefined(helperText)}>
      <Input
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder="(___) ___-____"
        value={formatPhoneTyping(value)}
        onChange={(e) => onChange(normalizePhoneDigits(e.target.value))}
        data-testid={testId}
      />
    </FormField>
  );
}

// ── funnel question copy (D7) ───────────────────────────────────────────

/** Reads one funnel question block's text — the funnel's resolved
 *  `useFunnelCopy().questions`, or the staff call's published copy. */
export type QuestionCopy = (block: FunnelQuestionKey) => string;

/** A block key assembled from parts, checked against the compiled defaults: a
 *  misspelt prefix, or an option value added without its block, throws rather
 *  than rendering a chip with no words on it. */
export function questionBlock(key: string): FunnelQuestionKey {
  if (!(key in FUNNEL_QUESTION_DEFAULTS)) throw new Error(`No funnel question block '${key}'`);
  return key as FunnelQuestionKey;
}

/** An option VALUE as its block key spells it: `FamilyMember` → `family-member`,
 *  `realEstateEquity` → `real-estate-equity`; a kebab value passes through. */
export function blockSlug(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

/** A catalog's options with each LABEL read from `<prefix>-<value>`. The values
 *  stay coded — they are the wire, and only the words are editable. */
export function optionsFromCopy(
  options: readonly ChoiceOption[],
  prefix: string,
  read: QuestionCopy,
): ChoiceOption[] {
  return options.map((o) => ({
    value: o.value,
    label: read(questionBlock(`${prefix}-${blockSlug(o.value)}`)),
  }));
}

/** A knockout with its statement, confirm question, restriction explanation and
 *  unsure note read from `knockout-<part>-<key>` wherever that block exists — the
 *  seven buyer knockouts. A knockout with no blocks (every seller one) keeps its
 *  coded text, and so does a part a knockout has no block for. */
export function knockoutWithCopy(q: KnockoutQuestionDef, read: QuestionCopy): KnockoutQuestionDef {
  const part = (name: string, coded: string | undefined): string | undefined => {
    const key = `knockout-${name}-${q.key}`;
    return key in FUNNEL_QUESTION_DEFAULTS ? read(key as FunnelQuestionKey) : coded;
  };
  return {
    ...q,
    statement: part("statement", q.statement) ?? "",
    restrictExplanation: part("restrict", q.restrictExplanation),
    unsureNote: part("unsure", q.unsureNote),
    followUp: q.followUp && {
      ...q.followUp,
      followUpQuestion: part("confirm", q.followUp.followUpQuestion) ?? "",
    },
  };
}

// ── knockout checklist ──────────────────────────────────────────────────

// Yes-first (prequal-v2 polarity: Yes is the favorable answer).
const KNOCKOUT_ANSWERS: ChoiceOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unsure", label: "Unsure" },
];

interface KnockoutChecklistProps {
  questions: KnockoutQuestionDef[];
  answers: Record<string, string>;
  onAnswer: (key: string, value: string) => void;
  /** The buyer knockouts' Document Language wording (D7): when given, each
   *  knockout's texts are read from its `knockout-*` blocks. The seller passes
   *  nothing and keeps its coded text; the shared chips and the intro stay
   *  coded for both (Q7). */
  copy?: QuestionCopy;
}

// Companion keys used to persist a restructured question's DISPLAY state
// (primary + follow-up selections) alongside its resolved engine value in the
// same knockouts map. The engine reads ONLY the bare key and ignores these
// (unknown keys are inert), so they round-trip through sessionStorage and the
// server harmlessly — this is what restores the correct branch (e.g. §E's
// "has obligations, all in good standing" vs "no obligations").
const primaryCompanionKey = (key: string) => `${key}__primary`;
const followUpCompanionKey = (key: string) => `${key}__followup`;

/** Below-question feedback keyed on the RESOLVED engine value (never on which
 *  control produced it) — so ONE render path covers plain confirm questions,
 *  the polarity-flipped excluded-industry pair, and the two conditional
 *  follow-up questions alike: a warm explanation when the answer restricts
 *  ("no"), a light educational note when it flags ("unsure"). */
function KnockoutFeedback({
  question,
  resolved,
}: {
  question: KnockoutQuestionDef;
  resolved: string | null;
}) {
  if (resolved === "no" && hasText(question.restrictExplanation)) {
    return (
      <p
        className="mt-space-3 mb-0 text-small text-ink bg-gold-tint border border-gold-soft rounded-md p-space-3"
        data-testid={`prequal-ko-${question.key}-explain`}
      >
        {question.restrictExplanation}
      </p>
    );
  }
  if (resolved === "unsure" && hasText(question.unsureNote)) {
    return (
      <p
        className="mt-space-3 mb-0 text-caption text-ink-mid"
        data-testid={`prequal-ko-${question.key}-unsure`}
      >
        {question.unsureNote}
      </p>
    );
  }
  return null;
}

interface RestructuredKnockoutProps {
  question: KnockoutQuestionDef;
  answers: Record<string, string>;
  onAnswer: (key: string, value: string) => void;
}

/** A knockout row that presents a PRIMARY control plus a conditional
 *  FOLLOW-UP control (§B/§C/§D/§E). It is stateless: primary + follow-up
 *  selections live in the knockouts map under companion keys, and the single
 *  engine-facing value (under the bare key) is DERIVED from them via the
 *  question's `resolve`. So the aggregate "needs a conversation" note reading
 *  `answers[key]` works exactly as for a plain knockout row. */
function RestructuredKnockout({ question, answers, onAnswer }: RestructuredKnockoutProps) {
  const cfg = question.followUp!;
  const primary = answers[primaryCompanionKey(question.key)] ?? null;
  const followUp = answers[followUpCompanionKey(question.key)] ?? null;
  const showFollowUp = primary === cfg.trigger;

  function commit(nextPrimary: string, nextFollowUp: string | null) {
    onAnswer(primaryCompanionKey(question.key), nextPrimary);
    onAnswer(followUpCompanionKey(question.key), nextFollowUp ?? "");
    onAnswer(question.key, cfg.resolve(nextPrimary, nextFollowUp) ?? "");
  }

  function handlePrimary(value: string) {
    // Leaving the trigger branch clears any stale follow-up selection.
    commit(value, value === cfg.trigger ? followUp : null);
  }

  function handleFollowUp(value: string) {
    // The "No, let me change my answer" branch resets the primary to the
    // clear state and hides the follow-up — never storing this value.
    if (cfg.resetOnFollowUp !== undefined && value === cfg.resetOnFollowUp) {
      commit(cfg.resetPrimaryTo ?? "", null);
      return;
    }
    commit(primary ?? "", value);
  }

  return (
    <div className="border border-line rounded-md p-space-3 bg-white-pure">
      {hasText(question.statement) && (
        <p className="text-small text-ink mt-0 mb-space-2">{question.statement}</p>
      )}
      {hasText(question.helperText) && (
        <p className="text-caption text-ink-mid mt-0 mb-space-2">{question.helperText}</p>
      )}
      {/* The knockout rows render their question as prose rather than a field
          label, so each group is named from the very same string that renders
          above it — primary from the statement, follow-up from its own
          question, which is the whole point of splitting them. */}
      <ChoiceButtons
        options={cfg.primaryOptions}
        value={primary}
        groupLabel={textOrUndefined(question.statement)}
        emphasize={cfg.emphasizePrimary}
        onChange={handlePrimary}
        testIdPrefix={`prequal-ko-${question.key}-primary`}
      />
      {showFollowUp && (
        <div className="mt-space-3 pl-space-3 border-l-2 border-gold-soft">
          {hasText(cfg.followUpQuestion) && (
            <p className="text-small text-ink mt-0 mb-space-2">{cfg.followUpQuestion}</p>
          )}
          <ChoiceButtons
            options={cfg.followUpOptions}
            value={followUp}
            groupLabel={textOrUndefined(cfg.followUpQuestion)}
            onChange={handleFollowUp}
            testIdPrefix={`prequal-ko-${question.key}-followup`}
          />
        </div>
      )}
      <KnockoutFeedback question={question} resolved={answers[question.key] ?? null} />
    </div>
  );
}

export function KnockoutChecklist({
  questions: coded,
  answers,
  onAnswer,
  copy,
}: KnockoutChecklistProps) {
  const questions = copy ? coded.map((q) => knockoutWithCopy(q, copy)) : coded;
  const anyNo = questions.some((q) => answers[q.key] === "no");
  const anyUnsure = questions.some((q) => answers[q.key] === "unsure");
  return (
    <div className="flex flex-col gap-space-4">
      <p className="text-small text-ink-mid m-0">
        A few quick questions — answer honestly. A “No” doesn’t end the
        conversation; it just means we talk before anything else.
      </p>
      {questions.map((q) =>
        q.followUp ? (
          <RestructuredKnockout
            key={q.key}
            question={q}
            answers={answers}
            onAnswer={onAnswer}
          />
        ) : (
          <div key={q.key} className="border border-line rounded-md p-space-3 bg-white-pure">
            {hasText(q.statement) && (
              <p className="text-small text-ink mt-0 mb-space-2">{q.statement}</p>
            )}
            {hasText(q.helperText) && (
              <p className="text-caption text-ink-mid mt-0 mb-space-2">{q.helperText}</p>
            )}
            <ChoiceButtons
              options={q.answerOptions ?? KNOCKOUT_ANSWERS}
              value={answers[q.key] ?? null}
              groupLabel={textOrUndefined(q.statement)}
              onChange={(v) => onAnswer(q.key, v)}
              testIdPrefix={`prequal-ko-${q.key}`}
            />
            <KnockoutFeedback question={q} resolved={answers[q.key] ?? null} />
          </div>
        ),
      )}
      {anyNo && (
        <p
          className="text-small text-ink-mid m-0"
          data-testid="prequal-fitcheck-note"
        >
          One or more of your answers above may affect your results — see the
          note next to each one for details.
        </p>
      )}
      {anyUnsure && !anyNo && <p className="text-caption text-ink-mid m-0">{UNSURE_NOTE}</p>}
    </div>
  );
}

// ── question card (§4.3) ────────────────────────────────────────────────

interface QuestionCardProps {
  stepNumber: number;
  section: string;
  title: string;
  hint?: string;
  children: React.ReactNode;
}

export function QuestionCard({ stepNumber, section, title, hint, children }: QuestionCardProps) {
  return (
    <section className={cn(CARD_SHELL, "p-space-4 sm:p-space-6")}>
      <p className={cn(OVERLINE, "mt-0 mb-space-2")}>
        Step {stepNumber} · {section}
      </p>
      {/* §7 — sans voice (Montserrat 600, sentence case); serif is reserved
          for rendered dollar figures. */}
      {/* A step title or hint published blank drops out (D4). */}
      {hasText(title) && (
        <h2
          className="font-semibold normal-case tracking-normal text-[1.45rem] leading-tight text-ink mt-0 mb-space-2"
          data-testid="prequal-step-title"
        >
          {title}
        </h2>
      )}
      {hasText(hint) && (
        <p className="text-small text-ink-mid mt-0 mb-space-4" data-testid="prequal-step-hint">
          {hint}
        </p>
      )}
      {children}
    </section>
  );
}

// ── progress bar + step pills (§4.2, fix §3.4/§3.5) ─────────────────────

export interface FunnelStep {
  key: string;
  label: string;
}

interface FunnelProgressProps {
  steps: FunnelStep[];
  currentIndex: number;
  /** Highest step index the visitor has reached — steps at or below it are
   *  clickable (the edit-and-redo loop). */
  maxReached: number;
  onSelect: (index: number) => void;
}

/** 4px gold gradient progress bar + the pill row: done = sage outline + ✓,
 *  current = ink pill with a gold number, upcoming = faint outline. All 7
 *  pills stay on one line at the wide (lg) width. */
export function FunnelProgress({ steps, currentIndex, maxReached, onSelect }: FunnelProgressProps) {
  const pct = ((currentIndex + 1) / steps.length) * 100;
  return (
    <div>
      <div className="h-[4px] bg-line rounded-pill overflow-hidden">
        <div
          className={cn(
            GOLD_GRADIENT,
            "h-full transition-[width] duration-500 ease-standard motion-reduce:transition-none",
          )}
          style={{ width: `${pct}%` }}
          data-testid="prequal-progress-fill"
        />
      </div>
      <ol
        className="flex flex-wrap lg:flex-nowrap items-center gap-space-1 list-none p-0 mt-space-3 mb-0"
        aria-label="Progress"
      >
        {steps.map((step, idx) => {
          const isActive = idx === currentIndex;
          const reachable = idx <= maxReached;
          const done = reachable && !isActive;
          return (
            <li key={step.key} className="min-w-0">
              <button
                type="button"
                disabled={!reachable}
                aria-current={isActive ? "step" : undefined}
                onClick={() => onSelect(idx)}
                data-testid={`prequal-step-${step.key}`}
                className={cn(
                  "rounded-pill px-space-2 py-space-1 min-h-space-6 sm:min-h-0 text-label whitespace-nowrap border transition-colors duration-fast ease-standard",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold",
                  isActive
                    ? "bg-ink-black border-ink-black text-white-pure font-semibold"
                    : done
                      ? "border-sage text-sage bg-white-pure cursor-pointer"
                      : "border-line text-ink-faint bg-transparent",
                )}
              >
                {isActive ? (
                  <>
                    <span className="text-gold">{idx + 1}</span> {step.label}
                  </>
                ) : done ? (
                  <>
                    <span aria-hidden="true">✓</span> {step.label}
                  </>
                ) : (
                  <>
                    {idx + 1}. {step.label}
                  </>
                )}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ── rail card 1: indicative range with count-up (§4.4) ──────────────────

interface SizingMeterProps {
  role: PrequalRole;
  result: PrequalEngineResult | null;
  /** The driving inputs exist (buyer: cash; seller: cash flow). */
  ready: boolean;
  /** 🔴 Whether the buyer is buying INTO a business they are already part of.
   *  Since prequal-v7 that arm is sized on the business on both schedules, so
   *  the meter shows the 20-year Maximum Financed Amount under a label that says
   *  so (D5). On the capital arm the headline is the MIDPOINT of the band
   *  rendered directly beneath it, so no ceiling is claimed there. Defaults
   *  false: the capital arm is the common case, and a caller that has not been
   *  told cannot claim a ceiling. */
  internalPurchase?: boolean;
}

/**
 * The rail's indicative range.
 *
 * 🔴 IT RENDERS NO FIGURE, NO BAND AND NO DECLINE BEFORE THE INPUT THAT SIZES
 * IT EXISTS. Until `ready`, it now says so in words rather than rendering
 * nothing — an absent card reads as one that failed to load, not as a question
 * that has not been asked yet. That pending state is what the internal arm sees
 * before its business cash flow is answered, and the two cases it replaces are
 * both wrong in ways a buyer cannot tell are wrong:
 *
 *   - a confident headline that is purely their own down payment reflected
 *     back, because a blank cash flow sizes a zero note; and
 *   - worse, `internal-buyer-below-program-minimum` — a DECLINE, live in the
 *     funnel (this is the client mirror, `prequalSizing.ts`, not a finalize-only
 *     verdict) — telling somebody who has simply not finished the form that the
 *     business supports less than the program minimum.
 */
export function SizingMeter({
  role,
  result,
  ready,
  internalPurchase = false,
}: SizingMeterProps) {
  const pass = ready && result && result.outcome === "Pass" ? result : null;
  const buyer = role === "Buyer";
  // prequal-v7 (D5): the seller and the internal buyer are sized on the business,
  // on both schedules; the live guide shows the 20-YEAR Maximum Financed Amount and
  // says, in C6's words, that the 10-year schedule finances less. The capital arm
  // keeps its cash-based headline and band (D3).
  const onSchedules = !buyer || internalPurchase;
  // ONE animation for the whole card — headline and band share a clock and a
  // fraction, so the band can never disagree with the figure sitting inside it
  // (the buyer's headline is clamped INTO its band by the engine, and that has
  // to stay visibly true on every frame, not just at rest).
  const [headline = null, bandLow = null, bandHigh = null] = useCountUpAll(
    onSchedules
      ? [pass?.maxFinanced20 ?? null, null, null]
      : [pass?.qualifiedAmount ?? null, pass?.buyingPowerLow ?? null, pass?.buyingPowerHigh ?? null],
  );
  const softDecline = ready && result && result.outcome === "SoftDecline";
  // Before the early return — hooks run unconditionally.
  const { indicativeLabel } = useFunnelCopy();

  // 🔴 THE CAPITAL BAND IS FLOORED AT BOTH ENDS. Since prequal-v7 the clamp holds
  // each figure's FINANCED part to the program minimum and adds the buyer's own
  // capacity back (D3), so a buyer whose capital sizes below it gets low === high
  // === capacity + the minimum, and a note of exactly the minimum. Rendering that
  // as "$258,000 – $258,000" presents a floor as a range. Compared against the
  // terms the sizing clamps to, not a literal.
  const bandEndsMeet =
    !onSchedules && pass != null && pass.buyingPowerLow === pass.buyingPowerHigh;
  const atProgramFloor =
    bandEndsMeet && pass?.noteAmount === PREQUAL_PROGRAM_TERMS.minAmountFinanced;

  // Pending: no figure, no band, no decline. `pass` and `softDecline` are both
  // already gated on `ready`, so this is the single place the not-yet state is
  // decided and there is no arrangement of the two below that can leak a figure
  // past it.
  if (!pass && !softDecline) {
    return (
      <div className={cn(CARD_SHELL, "overflow-hidden")} data-testid="prequal-sizing-meter">
        <div className={cn(GOLD_GRADIENT, "h-[3px]")} aria-hidden="true" />
        <div className="p-space-4 sm:p-space-5">
          <p className={cn(OVERLINE_MUTED, "mt-0 mb-space-2")}>Your indicative range</p>
          <p className="text-small text-ink-mid m-0" data-testid="prequal-meter-pending">
            {SIZING_METER_PENDING_NOTE}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(CARD_SHELL, "overflow-hidden")} data-testid="prequal-sizing-meter">
      <div className={cn(GOLD_GRADIENT, "h-[3px]")} aria-hidden="true" />
      <div className="p-space-4 sm:p-space-5">
        <p className={cn(OVERLINE_MUTED, "mt-0 mb-space-2")}>Your indicative range</p>
        {pass ? (
          onSchedules ? (
            <>
              <p
                className="font-serif text-[1.75rem] sm:text-[2.25rem] leading-none text-ink m-0"
                data-testid="prequal-meter-figure"
              >
                {/* A closed-form figure, so it shows cents (ruling Q1). */}
                {formatCurrency(headline, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p
                className="text-caption text-ink-mid mt-space-2 mb-0"
                data-testid="prequal-meter-schedule-label"
              >
                Maximum Financed Amount · 20-year schedule
              </p>
              <p
                className="text-caption text-ink-mid mt-space-1 mb-0"
                data-testid="prequal-meter-schedule-note"
              >
                {SIZING_METER_SCHEDULE_NOTE}
              </p>
            </>
          ) : (
            <>
              <p
                className="font-serif text-[1.75rem] sm:text-[2.25rem] leading-none text-ink m-0"
                data-testid="prequal-meter-figure"
              >
                {formatCurrency(headline)}
              </p>
              {pass.buyingPowerLow != null &&
                pass.buyingPowerHigh != null &&
                (atProgramFloor ? (
                  // ONE figure and the reason for it. The headline above already
                  // reads it; repeating it twice more with a dash between them is
                  // the part that misleads.
                  <p
                    className="text-caption text-ink-mid mt-space-2 mb-0"
                    data-testid="prequal-meter-program-floor"
                  >
                    {programMinimumBandNote(formatCurrency(PREQUAL_PROGRAM_TERMS.minAmountFinanced))}
                  </p>
                ) : bandEndsMeet ? null : (
                  // An equal-ended band at the $10,000,000 financed ceiling is
                  // suppressed for the same reason as at the floor: the headline
                  // already is that figure.
                  <p
                    className="text-caption text-ink-mid mt-space-2 mb-0"
                    data-testid="prequal-meter-band"
                  >
                    Estimated band {formatCurrency(bandLow)} – {formatCurrency(bandHigh)}
                  </p>
                ))}
              {/* 🔴 `firstTimerCapBound`, NOT `firstTimerCapApplied`. The
                  second says the RULE applies, which is true of every
                  first-timer including the many whose figures the cap never
                  touched; this caption tells the reader something was held
                  down, so it reads the flag that says something was. */}
              {pass.firstTimerCapBound && (
                <p
                  className="text-caption text-ink-mid mt-space-2 mb-0"
                  data-testid="prequal-firsttimer-note"
                >
                  {FIRST_TIMER_CAP_NOTE}
                </p>
              )}
            </>
          )
        ) : (
          <p className="text-small text-ink m-0">
            We’re not able to size this automatically yet — one or more answers
            above will need a quick conversation with our team first.
          </p>
        )}
        {/* The divider exists only to set the label off, so a blank published
            label takes it along (D4). */}
        {hasText(indicativeLabel) && (
          <>
            <div
              className="h-px bg-line my-space-3"
              aria-hidden="true"
              data-testid="prequal-meter-indicative-rule"
            />
            <p className="text-caption text-ink-mid m-0" data-testid="prequal-meter-indicative">
              {indicativeLabel}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ── rail card 2: program at a glance (§4.4) ─────────────────────────────

export function ProgramGlanceCard({
  role,
  sellerDownPaymentLabel,
}: {
  role: PrequalRole;
  /** Seller only — the down payment the seller typed, formatted. Absent, the row
   *  states the indicative 4–6% planning range (approval §2, M27). */
  sellerDownPaymentLabel?: string;
}) {
  const t = PREQUAL_PROGRAM_TERMS;
  const pct = (fraction: number) => `${Math.round(fraction * 100)}`;
  // Built from the terms the sizing clamps to, so the card cannot quote a range
  // the engine no longer finances. T1 is measured on the AMOUNT FINANCED.
  const rows: { label: string; value: string; note?: string }[] = [
    {
      label: "Program range",
      value: `${formatCompactCurrency(t.minAmountFinanced)} – ${formatCompactCurrency(t.maxAmountFinanced)} financed`,
    },
    role === "Seller"
      ? {
          label: "Buyer down payment",
          value:
            sellerDownPaymentLabel ??
            `Typically ${pct(t.downPaymentLowPct)}–${pct(t.downPaymentHighPct)}% · indicative only — you decide as note holder`,
        }
      : {
          label: "Typical down payment",
          value: `≈ ${pct(t.downPaymentMidpoint)}%`,
          note: "Indicative only — the note holder decides; Phoenix sets no minimum.",
        },
    {
      label: "Structure",
      value: `${pct(t.interestRate)}% · ${t.amortizationMonthsTenYear / 12} or ${t.amortizationMonthsTwentyYear / 12} yr · balloon yr ${t.balloonMonth / 12}`,
    },
    { label: "Administered by", value: "Phoenix Capital" },
  ];
  return (
    <div className={cn(CARD_SHELL, "p-space-4 sm:p-space-5")} data-testid="prequal-glance">
      <p className={cn(OVERLINE_MUTED, "mt-0 mb-space-1")}>Program at a glance</p>
      <dl className="m-0">
        {rows.map(({ label, value, note }, i) => (
          <div key={label} className={cn("py-space-2", i > 0 && "border-t border-line")}>
            <div className="flex items-baseline justify-between gap-space-3">
              {/* min-w-0 lets a long label wrap instead of shouldering the
                  figure off the right edge of a phone-width card. */}
              <dt className="text-caption text-ink-mid min-w-0">{label}</dt>
              <dd className="font-serif font-bold text-small text-ink m-0 text-right">{value}</dd>
            </div>
            {note && (
              <p className="text-caption text-ink-mid mt-space-1 mb-0" data-testid="prequal-glance-note">
                {note}
              </p>
            )}
          </div>
        ))}
      </dl>
    </div>
  );
}

// ── rail card 3: AI guide (§4.4) ────────────────────────────────────────

const MONEY_SPLIT_RE = /(\$[\d,.]+[MKk]?)/;

/** Bold any $figures in the (plain-text) tip prose. Split with a single
 *  capture group — odd indices are the money matches. */
function renderTipProse(text: string) {
  return text.split(MONEY_SPLIT_RE).map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="text-gold-deep">
        {part}
      </strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

interface GuidePanelProps {
  text: string | null;
  /** §2 — manual "↻ Update tip" affordance; hidden when not wired. */
  onRefresh?: () => void;
  /** Disables the refresh control while a tip request is in flight. */
  refreshing?: boolean;
}

/** Hidden entirely when there is no prose for the CURRENT step (AI
 *  unavailable / failed) — it never blocks navigation. */
export function GuidePanel({ text, onRefresh, refreshing }: GuidePanelProps) {
  if (!text) return null;
  return (
    <div className={cn(CARD_SHELL, "p-space-3 sm:p-space-4")} data-testid="prequal-guide-panel">
      <div className="flex items-center gap-space-2">
        <span
          className="w-8 h-8 rounded-full bg-ink-black text-gold flex items-center justify-center shrink-0"
          aria-hidden="true"
        >
          ✦
        </span>
        <span className={OVERLINE_MUTED}>Your guide</span>
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Update tip"
            data-testid="prequal-guide-refresh"
            className="ml-auto shrink-0 bg-transparent border-0 p-0 text-caption text-ink-mid hover:text-gold-deep cursor-pointer disabled:opacity-50 disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-sm"
          >
            ↻ Update tip
          </button>
        )}
      </div>
      <p className="text-small text-ink mt-space-2 mb-0 whitespace-pre-wrap">
        {renderTipProse(text)}
      </p>
    </div>
  );
}

// ── result ceremony (§4.5) ──────────────────────────────────────────────

function GoldSeal() {
  return (
    <svg
      viewBox="0 0 96 96"
      className="w-24 h-24 mx-auto mb-space-3 text-gold"
      aria-hidden="true"
      data-testid="prequal-seal"
    >
      <circle cx="48" cy="48" r="44" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="48" cy="48" r="37" fill="none" stroke="currentColor" strokeWidth="1" />
      <path
        d="M34 50l10 10 18-23"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ResultHero({
  role,
  result,
  /**
   * D5 — whether this is an INTERNAL purchase, sized on the business rather than
   * on the buyer's capital.
   *
   * 🔴 **THE HERO WAS THE D5 VIOLATION, not the documents.** Both of its buyer
   * lines describe a wallet: the label reads "Indicative buying power" and the
   * context line reads "depending on the final down payment". Neither is true of
   * an internal purchase — the figure comes from the free cash flow of the
   * business, and the down payment can legitimately be nothing — and this is the
   * first screen the applicant sees, before any document.
   *
   * Keyed on the PATH the server carried back, exactly as
   * `DocumentCeremonyCards` keys its own arm: absent reads as the capital path,
   * so every submission from before the fork renders what it always did.
   */
  internalPurchase = false,
}: {
  role: PrequalRole;
  result: PrequalEngineResult | null;
  internalPurchase?: boolean;
}) {
  const buyer = role === "Buyer";
  const { indicativeLabel } = useFunnelCopy();
  const t = PREQUAL_PROGRAM_TERMS;
  // prequal-v7 (D2/D5): the seller and the internal buyer see both schedules side
  // by side. A row sized before v7 carries no schedule figures and keeps today's
  // one-figure hero, so a stored result reads as it did (D15).
  const scheduled = (!buyer || internalPurchase) && result?.maxFinanced20 != null;
  const low = result?.buyingPowerLow ?? null;
  const high = result?.buyingPowerHigh ?? null;
  const minimum = formatCurrency(t.minAmountFinanced);

  // The capital arm (D3): the headline keeps its cash-based band. Its ends meet at
  // the program floor (the minimum financed plus the buyer's own capacity), which
  // is one figure and the reason for it, never "$258,000 – $258,000"; at the
  // $10,000,000 ceiling they meet too, and the band simply drops.
  const capitalAtFloor =
    low != null && high != null && low === high && result?.noteAmount === t.minAmountFinanced;
  const capitalContext =
    low == null || high == null
      ? null
      : capitalAtFloor
        ? programMinimumBandNote(minimum)
        : low === high
          ? null
          : `Estimated band ${formatCurrency(low)} – ${formatCurrency(high)} depending on the final down payment.`;

  // The seller's named asking price (ruling Q10 (b)): the amount financed it
  // implies, against each schedule's Maximum — a fact, never a ratio (D2 keeps the
  // coverage ratios staff-only).
  const stated = result?.statedAmountFinanced ?? null;
  const askingLine =
    !buyer && scheduled && stated != null && stated > 0
      ? `At your asking price of ${formatCurrency(stated + (result?.downPaymentAmount ?? 0))}, the amount financed would be ${formatCurrency(stated)}: `
        + `${result?.statedWithinMaximum20 ? "within" : "above"} the 20-year maximum and `
        + `${result?.statedWithinMaximum10 ? "within" : "above"} the 10-year maximum.`
      : null;

  // A pre-v7 stored result (no schedule figures): today's wording, unchanged.
  const legacyContext = buyer
    ? internalPurchase
      ? low != null && high != null && low !== high
        ? `Estimated band ${formatCurrency(low)} – ${formatCurrency(high)} — sized `
          + `from the cash flow the business produces, at program terms.`
        : "Sized from the cash flow the business produces, at program terms."
      : null
    : result?.coverageAtAsking != null
      ? `Indicative coverage at your asking price: ${result.coverageAtAsking.toFixed(2)}x.`
      : "Sized from the cash flow your business produces, at program terms.";

  const scheduleColumn = (
    label: string,
    figure: number | null | undefined,
    belowMinimum: boolean | null | undefined,
    testId: string,
  ) => (
    <div className="min-w-0" data-testid={testId}>
      <p className={cn(OVERLINE_MUTED, "mt-0 mb-space-2")}>{label}</p>
      {belowMinimum ? (
        <p className="text-small text-ink-mid m-0" data-testid={`${testId}-below-minimum`}>
          {belowProgramMinimumOnSchedule(minimum)}
        </p>
      ) : (
        <p
          className="font-serif text-ink m-0 leading-none text-[1.75rem] sm:text-[2.25rem]"
          data-testid={`${testId}-figure`}
        >
          {/* Closed-form figures show cents on a screen (ruling Q1). */}
          {formatCurrency(figure ?? null, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
      )}
    </div>
  );

  return (
    <div className={cn(CARD_SHELL, "p-space-4 sm:p-space-6 text-center")}>
      <GoldSeal />
      <p className={cn(OVERLINE, "mt-0 mb-space-2")}>
        {buyer ? "Prequalified" : "Business prequalified"}
      </p>
      {scheduled ? (
        <>
          <p className="text-label uppercase tracking-wide text-ink-mid mt-0 mb-space-3">
            Maximum Financed Amount
          </p>
          {/* Side by side from 480px, stacked below it: two serif figures with
              cents need the full width each on a phone. */}
          <div
            className="grid grid-cols-1 min-[480px]:grid-cols-2 gap-space-4"
            data-testid="prequal-result-schedules"
          >
            {scheduleColumn("20-year schedule", result?.maxFinanced20, result?.belowMinimum20, "prequal-result-20")}
            {scheduleColumn("10-year schedule", result?.maxFinanced10, result?.belowMinimum10, "prequal-result-10")}
          </div>
          <p className="text-small text-ink-mid mt-space-4 mb-0" data-testid="prequal-result-sizing-rule">
            {PROGRAM_SIZING_RULE}
          </p>
          {askingLine && (
            <p className="text-small text-ink-mid mt-space-3 mb-0" data-testid="prequal-result-asking">
              {askingLine}
            </p>
          )}
          {buyer && (
            <p className="text-small text-ink-mid mt-space-3 mb-0" data-testid="prequal-result-down-payment">
              {DOWN_PAYMENT_SECOND_PERSON}
            </p>
          )}
        </>
      ) : (
        <>
          <p className="text-label uppercase tracking-wide text-ink-mid mt-0 mb-space-2">
            {/* "Qualified purchase price" is the name the Letter's At-a-Glance row
                gave this figure on the internal path before prequal-v7; a stored
                pre-v7 internal result keeps it. */}
            {buyer
              ? internalPurchase
                ? "Qualified purchase price · Up to"
                : // 🔴 NO "Up to" HERE. On the capital arm the headline is the
                  // MIDPOINT of the buying-power band — derived at the 5% midpoint
                  // while the band's top comes from the 4% low — so it is not a
                  // ceiling and the engine's own high end exceeds it.
                  "Indicative buying power"
              : "Indicative financeable range"}
          </p>
          <p
            className={cn(
              "font-serif text-ink m-0 leading-none",
              // The pre-v7 seller variant renders a full range — it needs the
              // deeper step-down to stay on one phone line.
              buyer ? "text-[2.25rem] sm:text-[3.25rem]" : "text-[1.3rem] sm:text-[2.75rem]",
            )}
            data-testid="prequal-result-figure"
          >
            {buyer
              ? formatCurrency(result?.qualifiedAmount ?? null)
              : `${formatCurrency(result?.supportablePriceLow ?? null)} – ${formatCurrency(
                  result?.supportablePriceHigh ?? null,
                )}`}
          </p>
          {buyer && !internalPurchase ? (
            <>
              {capitalContext && (
                <p className="text-small text-ink-mid mt-space-3 mb-0" data-testid="prequal-result-context">
                  {capitalContext}
                </p>
              )}
              {/* C5 under the capital figure, matching the letter (D5). */}
              <p
                className="text-caption text-ink-mid mt-space-3 mb-0"
                data-testid="prequal-result-rate-structure"
              >
                {PROGRAM_RATE_AND_STRUCTURE}
              </p>
            </>
          ) : (
            legacyContext && (
              <p className="text-small text-ink-mid mt-space-3 mb-0" data-testid="prequal-result-context">
                {legacyContext}
              </p>
            )
          )}
        </>
      )}
      {/* The rule separates the figure from the label; a blank published label
          takes it along (D4). */}
      {hasText(indicativeLabel) && (
        <>
          <div
            className="h-[3px] w-16 bg-gold mx-auto mt-space-4 mb-space-3"
            aria-hidden="true"
            data-testid="prequal-hero-indicative-rule"
          />
          <p className="text-caption text-ink-mid m-0" data-testid="prequal-hero-indicative">
            {indicativeLabel}
          </p>
        </>
      )}
    </div>
  );
}

export function ReferenceStrip({
  referenceNumber,
  validThroughUtc,
}: {
  referenceNumber?: string | null;
  validThroughUtc?: string | null;
}) {
  if (!referenceNumber && !validThroughUtc) return null;
  return (
    <p
      className="text-[10px] uppercase tracking-[0.24em] text-ink-mid text-center m-0"
      data-testid="prequal-reference-strip"
    >
      {referenceNumber && <>Reference {referenceNumber}</>}
      {referenceNumber && validThroughUtc && " · "}
      {validThroughUtc && <>Valid through {formatDateInTz(validThroughUtc)}</>}
    </p>
  );
}

function PaperThumb() {
  return (
    <div
      className="w-12 h-16 border border-line rounded-sm bg-white-pure overflow-hidden shrink-0"
      aria-hidden="true"
    >
      <div className="h-3 bg-ink-black" />
      <div className="mt-2 mx-2 h-px bg-line" />
      <div className="mt-2 mx-2 h-px bg-line" />
      <div className="mt-2 mx-2 h-px bg-line" />
    </div>
  );
}

interface CeremonyDocCard {
  /** Stable react key, and — for a GENERATED document — the `docType` to match in
   *  the finalize result's `documents`. The brochure is not a generated document
   *  and carries no match; it supplies {@link href} instead. */
  docType: string;
  title: string;
  blurb: string;
  /** Prospect-only analysis document — a distinct "private, not for
   *  distribution" treatment vs. the shareable letter / deal sheet. */
  isPrivate?: boolean;
  /**
   * A link this card already has, superseding the `documents` lookup.
   *
   * Set for the PROGRAM BROCHURE only. That file is not a per-submission
   * artifact — it is one uploaded PDF per role, Phoenix-wide, resolved from its
   * own endpoint — so there is no row in `documents` to find it by, and a card
   * that fell through to that lookup would render the disabled "Preview · PDF"
   * placeholder forever.
   */
  href?: string;
}

/** 2-up document cards — disabled `PREVIEW · PDF` before finalize; once the
 *  finalize result arrives with SAS links they flip live (View / Download). */
export function DocumentCeremonyCards({
  role,
  finalized,
  base,
  freshDocuments,
  brochureUrl,
}: {
  role: PrequalRole;
  // 🔴 THE ENGINE RESULT IS DELIBERATELY NOT A PROP, and re-adding one is how
  // the figure defect comes back. Its only reader was the Deal Sheet card's
  // `qualifiedAmount` fallback — a capacity figure standing in for the price
  // the linked document prints. Every number a card names now comes off
  // `finalized`, which is the same object the server priced the document from.
  finalized: PrequalFinalizeResult | null;
  /** Funnel base path (`/prequalify` or `/prequalify/{slug}`) — the
   *  Model-a-deal card links to `{base}/explore` so it stays in the current
   *  native/partner namespace. */
  base: string;
  /**
   * Links re-fetched from the server on mount, superseding the ones the finalize
   * result carried.
   *
   * 🔴 **This is what keeps the download buttons working.** A finalize link is a
   * 15-minute blob SAS and the finalize result is replayed out of sessionStorage
   * forever, so without a re-fetch every button here is dead a quarter of an hour
   * after the person finished — and still offered.
   *
   * `null` means "nothing fresher available" (no result token, an expired one, or
   * the read failed) and falls back to the stored links, which is exactly what
   * this component did before. Never an empty array — see
   * `useFreshDocumentLinks`.
   */
  freshDocuments?: PrequalDocumentLink[] | null;
  /** The Program Brochure's URL, or null/absent when the role has none uploaded.
   *  Null HIDES the fourth card — no placeholder, no broken link, no error. */
  brochureUrl?: string | null;
}) {
  // 🔴 A LETTER-ONLY BUYER IS NOT OFFERED A DEAL SHEET CARD. The buyer list is
  // otherwise a static array, so that applicant was shown a "Deal Sheet" card
  // with a dead, greyed-out Preview button — which reads as "your Deal Sheet
  // failed" on the one surface the BUYER sees, and is the exact misreading the
  // staff list's "Letter only, by request" line was added to prevent.
  //
  // Keyed on the CHOICE the server carries back, never on the absence of a link.
  // Presence cannot tell "never asked for" from "failed to generate" and the two
  // want opposite treatments: a buyer who DID ask keeps the card, because the
  // dead one is their only sign anything is wrong.
  //
  // Only an EXPLICIT LetterOnly suppresses, the same compatibility rule every
  // server-side reader of this column applies: a submission finalized before the
  // fork existed carries null and keeps the card it has always had. A SELLER
  // carries null too, but never reaches this line — the seller cards are the
  // other arm of the ternary below — so the rule that protects them is the
  // single writer in `PrequalificationIntakeService`, which forces null unless
  // the role is Buyer, not this one.
  const letterOnly = finalized?.buyerDocumentPath === "LetterOnly";
  // D5, on the two cards a buyer reads before opening either document. Same
  // keying rule as `letterOnly` above and for the same reason: the CHOICE the
  // server carried back, never the presence or absence of a document.
  const internalPurchase = finalized?.buyerDocumentPath === "InternalBuyer";

  // 🔴 THE PRICE THE DEAL SHEET IS ACTUALLY BUILT ON, read from the finalize
  // result and from nothing else.
  //
  // This card used to print `result.qualifiedAmount` — a CAPACITY headline,
  // sized from the buyer's wallet — while the document it links to is priced
  // `namedPrice ?? qualifiedAmount`. One submission, two numbers, the card
  // naming a deal the document does not. Measured on a live staging run: the
  // card said $3,610,000 and the document said $9,000,000.
  //
  // Server-computed, through the same shared rule (`PrequalDealSheetPricing`)
  // that prices the document, so the two cannot answer differently. Deriving it
  // here from the stored answers would be a second implementation of one money
  // question.
  //
  // ⚠️ NOT because of a cross-device reader: this page redirects to the funnel
  // when `loadFunnelState` comes back empty, so that reader never gets here. The
  // drift argument is the whole reason and is sufficient by itself.
  const dealSheetPrice = finalized?.dealSheetPrice ?? null;

  // 🔴 NULL NAMES NO FIGURE, AND IT MUST NOT FALL BACK TO `qualifiedAmount`.
  // That fallback IS the defect — and it fires on precisely the rows where the
  // two disagree, so it would be invisible on every row where they happen to
  // match. The no-figure sentence is written to read as a sentence rather than
  // as a gap: `formatCurrency(null)` returns "", so the arms cannot be one
  // template with a blank in it.
  //
  // "What you pay" reads as the down payment, which on an internal purchase is
  // routinely nothing — the premise of that path. The internal wording drops
  // that clause and keeps the other two, which are true on both arms. It is
  // factored out so the distinction survives on BOTH sentences: a fix that
  // carried it only on the priced arm would trade one wrong card for another.
  const dealSheetContents = internalPurchase
    ? "what is financed and what you owe each month"
    : "what you pay, finance and owe each month";
  const dealSheetBlurb =
    dealSheetPrice === null
      ? `Your side of this deal — ${dealSheetContents}. Not an offer to a seller.`
      : `Your side of a deal at ${formatCurrency(dealSheetPrice)} — ${dealSheetContents}.`
        + " Not an offer to a seller.";

  // 🔴 A CARD DESCRIBES THE DOCUMENT IN THE READER'S HAND, AND SAYING THE WRONG
  // THING ABOUT IT IS WORSE THAN SAYING NOTHING. Both buyer cards below have
  // broken that rule and both are held to it here: the Letter card claimed
  // "proof of buying power" on a path whose letter makes no claim about the
  // buyer's wallet, and the Deal Sheet card named a capacity figure beside a
  // link to a document priced on something else. Where the honest value is
  // missing, each card drops the clause rather than substituting a plausible
  // one — which is why the Deal Sheet's no-figure sentence exists above.
  const cards: CeremonyDocCard[] =
    role === "Buyer"
      ? [
          {
            docType: "Letter",
            title: "Prequalification Letter",
            // The Letter's instance of the rule above. "Proof of buying power"
            // is a claim about the BUYER'S WALLET, and on an internal purchase
            // the letter makes no such claim — it says the business supports the
            // purchase. The internal wording states the basis positively,
            // because "we did not look at your savings" is the single most
            // useful fact this applicant can be told about their own letter.
            blurb: internalPurchase
              ? "Phoenix's written confirmation that the purchase is qualified — "
                + "sized on the business's cash flow, not on your own capital."
              : "Your proof of buying power — hand it to a seller or their broker.",
          },
          ...(letterOnly ? [] : [{
            docType: "DealSheet" as const,
            title: "Deal Sheet",
            // 🔴 WHOSE SIDE, and what it is NOT. A document called "Deal Sheet"
            // reads like something to hand a seller, and the one thing it is not
            // is an offer — so the card says so where a buyer decides what to do
            // with it, and the document's own header says the same.
            //
            // NOT `isPrivate`, and that is a constraint rather than an omission:
            // the sourcing partner's portal serves every prequalification
            // document except the Analysis, so a partner broker can open this
            // sheet and any "for your eyes" treatment would be a promise the
            // platform does not keep.
            // "Not an offer" stays on both arms: on an internal purchase the
            // counterparty is a relative, an employer or a partner, and a
            // document called a Deal Sheet arriving from a lender is exactly as
            // easy to mistake for an offer across a kitchen table as across a
            // broker's desk.
            //
            // The figure and the internal wording are both resolved above — see
            // `dealSheetPrice` and `dealSheetContents`.
            blurb: dealSheetBlurb,
          }]),
        ]
      : [
          {
            docType: "Letter",
            title: "Business Prequalification Letter",
            blurb:
              "Your proof the business is screened and underwritable — share it with buyers and brokers.",
          },
          {
            docType: "DealSheet",
            title: "Deal Sheet",
            blurb:
              // prequal-v4: there is no month-12 liquidity event. The note is
              // contributed to the pool after a short transition and the seller
              // holds a proportional pool interest, so this card names what the
              // document now actually contains.
              //
              // 🔴 "(for your eyes)" was REMOVED, and it was not a nit: the
              // sourcing partner's portal serves every prequalification document
              // except the Analysis, so a partner broker can open this sheet. The
              // phrase promised a confidentiality the platform does not provide.
              // The Analysis is the document that genuinely is private, and it
              // carries the `isPrivate` treatment for exactly that reason.
              "Your price range on the 20- and 10-year schedules, and what the program costs you.",
          },
        ];

  // The private analysis document is per-portal toggle-gated, so it only exists
  // in the finalize result when its portal has it enabled. Show the card only
  // then — never a disabled placeholder (we can't know pre-finalize whether it
  // will be generated).
  const hasAnalysis = finalized?.documents.some((d) => d.docType === "Analysis") ?? false;
  if (hasAnalysis) {
    cards.push({
      docType: "Analysis",
      title: role === "Buyer" ? "Prequalification Analysis" : "Business Readiness Analysis",
      blurb:
        role === "Buyer"
          ? "How we calculated your buying power and your path to approval."
          : "Your readiness scorecard and how to strengthen your deal.",
      isPrivate: true,
    });
  }

  // The Program Brochure — the program explanation the deal sheets and analyses
  // stopped carrying, as one uploaded PDF per role.
  //
  // 🔴 ABSENT when no brochure is uploaded for this role, and that is the COMMON
  // case rather than an edge one: nothing is seeded and no PDF ships in the
  // repository, so it is what every fresh environment reads. Follows the Analysis
  // card's conditional push for the same reason — a card we cannot serve is worse
  // than no card, whether it renders a dead link or a placeholder explaining
  // itself.
  //
  // NOT `isPrivate`: that treatment says "for your reference, not for
  // distribution", and this is a public marketing document anyone may forward.
  if (brochureUrl) {
    cards.push({
      docType: "Brochure",
      title: "Program Brochure",
      blurb: "How the program works, in full.",
      href: brochureUrl,
    });
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-4">
      {cards.map((card) => {
        // A card's own href wins — it is the brochure, which has no row in
        // `documents` to be found by. Otherwise: the RE-FETCHED links first, then
        // the finalize result's own. The order is the whole fix: the stored links
        // expire fifteen minutes after finalize and this page outlives them.
        const url =
          card.href
          ?? freshDocuments?.find((d) => d.docType === card.docType)?.url
          ?? finalized?.documents.find((d) => d.docType === card.docType)?.url
          ?? null;
        return (
          <div
            key={card.docType}
            className={cn(
              CARD_SHELL,
              "p-space-4 flex flex-col gap-space-3",
              card.isPrivate && "border-gold bg-gold-tint",
            )}
          >
            <div className="flex items-start gap-space-3">
              <PaperThumb />
              <div className="min-w-0">
                {card.isPrivate && (
                  <span className="inline-block text-label uppercase tracking-wide text-gold-deep mb-space-1">
                    Private · for your reference
                  </span>
                )}
                <p className="text-small text-ink font-semibold mt-0 mb-space-1">{card.title}</p>
                <p className="text-caption text-ink-mid m-0">{card.blurb}</p>
              </div>
            </div>
            {url ? (
              <div className="flex gap-space-2 mt-auto">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => window.open(url, "_blank", "noopener")}
                >
                  View
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => window.open(url, "_blank", "noopener")}
                >
                  Download
                </Button>
              </div>
            ) : (
              <button
                type="button"
                disabled
                className="self-start text-label uppercase tracking-wide text-gold-deep opacity-50 bg-transparent border-0 p-0 cursor-not-allowed mt-auto"
              >
                Preview · PDF
              </button>
            )}
          </div>
        );
      })}
      {role === "Buyer" && (
        <Link
          to={`${base}/explore`}
          data-testid="prequal-model-a-deal"
          className={cn(
            CARD_SHELL,
            "sm:col-span-2 p-space-4 flex items-start gap-space-3 no-underline",
            "hover:border-gold hover:bg-gold-tint transition-colors duration-fast ease-standard",
          )}
        >
          <span
            className="w-12 h-16 border border-line rounded-sm bg-white-pure flex items-center justify-center text-gold-deep text-[1.35rem] shrink-0"
            aria-hidden="true"
          >
            ⚖
          </span>
          <span className="min-w-0">
            <span className="block text-small text-ink font-semibold mb-space-1">Model a deal</span>
            <span className="block text-caption text-ink-mid">
              Try different prices and cash flows to see your numbers.
            </span>
          </span>
        </Link>
      )}
    </div>
  );
}

// ── finalize step (§5 — the LAST FORM STEP, both roles) ─────────────────

export interface FinalizeContact {
  firstName: string;
  lastName: string;
  email: string;
  /** Bare digits (max 10), a leading country code already absorbed by
   *  `normalizePhoneDigits` — the PhoneField mask is display-only. */
  phone: string;
}

interface FinalizeSectionProps {
  /** Buyer/seller — selects the path-dynamic consent + fine-print copy. */
  role: PrequalRole;
  /** Client-side computed result — drives the soft-decline messaging. */
  result: PrequalEngineResult | null;
  /** Controlled contact fields — live on the parent funnel form so they
   *  persist to sessionStorage with the rest of the answers. */
  contact: FinalizeContact;
  onContactChange: (patch: Partial<FinalizeContact>) => void;
  buildRequest: (extra: {
    consentAccepted: boolean;
    website: string;
  }) => PrequalFinalizeRequest;
  /** How the request is sent. Defaults to the plain finalize call; the funnel
   *  pages pass their persistence adapter's finalize so that when a server
   *  draft backs the session, the submission and the draft's deletion happen
   *  in one transaction instead of leaving a finished applicant on an
   *  abandoned-draft list. */
  submit?: (request: PrequalFinalizeRequest) => Promise<PrequalFinalizeResult>;
  /** Success — the parent persists the result and navigates to the Result
   *  route. */
  onFinalized: (res: PrequalFinalizeResult) => void;
  /** A SLOT rendered directly above the submit row, and nothing more: this
   *  component neither knows nor asks what goes in it.
   *
   *  The assisted surface fills it with its certification controls so they sit
   *  beside the button they describe. The public funnel passes nothing and
   *  renders exactly what it always did — which is why this is a slot and not
   *  an `if (assisted)` branch. A branch here would fork the one component
   *  decision 18 exists to keep shared, on the first divergent bug fix. */
  beforeSubmit?: ReactNode;
  /** True when the visitor already gave a phone number before reaching the
   *  funnel — today, only the `/buy` landing page, which requires BOTH a valid
   *  number and SMS consent on the same step before it will continue.
   *
   *  It changes one thing: the phone field stops describing itself as optional.
   *  For that visitor "Optional — if you'd like us to call" is simply stale —
   *  they gave the number and opted in minutes ago, and reading it invites them
   *  to wonder whether the number they already provided went missing.
   *
   *  🔴 It does NOT make the field required. They may still clear it, and
   *  clearing it must keep working: a visitor removing a number is making a
   *  decision, not failing a validation. The only change is the copy. */
  phoneAlreadyGiven?: boolean;
  /** Extra identity fields rendered after Phone and before the honeypot — the
   *  buyer funnel's acquiring-company details for a company or sponsor buyer
   *  (owner brief D2). A SLOT for the same reason as `beforeSubmit`: unset, the
   *  seller funnel and an individual buyer render exactly what they always
   *  did. */
  contactExtras?: ReactNode;
  /** Runs in submit after the name check, beside the other client checks; a
   *  string is the refusal shown in this form's error slot. Paired with
   *  `contactExtras` — the fields it checks are the ones that slot renders. */
  validateExtras?: () => string | null;
}

export function FinalizeSection({
  role,
  result,
  contact,
  onContactChange,
  buildRequest,
  submit,
  onFinalized,
  beforeSubmit,
  phoneAlreadyGiven = false,
  contactExtras,
  validateExtras,
}: FinalizeSectionProps) {
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot — stays empty for humans
  const [error, setError] = useState<string | null>(null);

  const softDecline = result?.outcome === "SoftDecline";

  // Server-published wording, falling back to the compiled-in constants. Always
  // populated on the first frame — the compiled-in sentence shows until the
  // server's lands, and finalize never waits on it. Only a block PUBLISHED blank
  // (which the editor refuses for these two; legacy data only) leaves the
  // checkbox without a sentence, and then it drops the empty element (D4). The
  // assisted surface renders THIS component, so a published edit reaches the
  // broker-driven form and the applicant's own form through one code path.
  const funnelCopy = useFunnelCopy();
  const consentLine =
    role === "Seller" ? funnelCopy.consentLineSeller : funnelCopy.consentLineBuyer;
  const finePrint = role === "Seller" ? funnelCopy.finePrintSeller : funnelCopy.finePrintBuyer;
  // The buyer's contact labels are Document Language blocks (D7); the seller
  // keeps its coded labels. A blank published label drops with its asterisk.
  const q = funnelCopy.questions;
  const buyer = role === "Buyer";
  const contactCopy = {
    firstName: buyer ? q["label-buyer-first-name"] : "First name",
    lastName: buyer ? q["label-buyer-last-name"] : "Last name",
    email: buyer ? q["label-buyer-email"] : "Email",
    emailHelper: buyer ? q["helper-buyer-email"] : "Where your results and documents go.",
    phone: buyer ? q["label-buyer-phone"] : "Phone",
    phoneHelper: phoneAlreadyGiven
      ? buyer
        ? q["helper-buyer-phone-given"]
        : "From the details you just gave us."
      : buyer
        ? q["helper-buyer-phone"]
        : "Optional — if you'd like us to call.",
  };

  const mutation = useSubmittingMutation(
    async () => {
      const send = submit ?? prequalificationApi.finalize;
      // Attached HERE rather than in each page's buildRequest, because this is
      // the one send point every funnel path goes through — the anonymous
      // finalize, the draft finalize and the assisted surface all render this
      // component. Read from storage at submit time, not at mount: the capture
      // happens on the landing page, several steps earlier.
      const attribution = loadAttribution() ?? undefined;
      const res = await send({
        ...buildRequest({ consentAccepted: consent, website }),
        attribution,
      });
      onFinalized(res);
    },
    {
      onError: (err) => {
        // §3.3 — never surface raw HTTP text; keep genuine server
        // validation messages (consent / email). Log the real error.
        console.error(err);
        const raw = err instanceof Error ? err.message.trim() : "";
        setError(!raw || /HTTP \d+|Request failed/i.test(raw) ? FRIENDLY_FINALIZE_ERROR : raw);
      },
    },
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!contact.firstName.trim() || !contact.lastName.trim()) {
      setError("First and last name are required.");
      return;
    }
    const extrasProblem = validateExtras?.() ?? null;
    if (extrasProblem) {
      setError(extrasProblem);
      return;
    }
    if (!/\S+@\S+\.\S+/.test(contact.email)) {
      setError("Enter a valid email address.");
      return;
    }
    if (!consent) {
      setError("Please accept the consent statement to continue.");
      return;
    }
    void mutation.submit();
  }

  return (
    <div className="flex flex-col gap-space-4">
      {softDecline && (
        <div className={cn(CARD_SHELL, "p-space-5")} data-testid="prequal-softdecline">
          <h3 className="font-semibold normal-case tracking-normal text-[1.25rem] text-ink mt-0 mb-space-2">
            Your next step
          </h3>
          <p className="text-small text-ink-mid m-0">
            Thanks for walking through this — based on your answers, the next
            step is a conversation with our team rather than an instant result.
            Finalize below and we’ll be in touch soon.
          </p>
        </div>
      )}

      {!softDecline && result && result.unsureFlags.length > 0 && (
        <p className="text-caption text-ink-mid m-0">{UNSURE_NOTE}</p>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-space-3" noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
          <FormField label={textOrUndefined(contactCopy.firstName)} required>
            <Input
              value={contact.firstName}
              onChange={(e) => onContactChange({ firstName: e.target.value })}
              data-testid="prequal-first-name"
            />
          </FormField>
          <FormField label={textOrUndefined(contactCopy.lastName)} required>
            <Input
              value={contact.lastName}
              onChange={(e) => onContactChange({ lastName: e.target.value })}
              data-testid="prequal-last-name"
            />
          </FormField>
        </div>
        <FormField
          label={textOrUndefined(contactCopy.email)}
          required
          helperText={textOrUndefined(contactCopy.emailHelper)}
        >
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={contact.email}
            onChange={(e) => onContactChange({ email: e.target.value })}
            data-testid="prequal-finalize-email"
          />
        </FormField>
        <PhoneField
          label={contactCopy.phone}
          helperText={contactCopy.phoneHelper}
          value={contact.phone}
          onChange={(digits) => onContactChange({ phone: digits })}
          testId="prequal-finalize-phone"
        />

        {contactExtras}

        {/* Honeypot — visually hidden off-screen (never display:none-only);
            real visitors never see or fill it. */}
        <div className="sr-only" aria-hidden="true">
          <label>
            Website
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </label>
        </div>

        <label className="flex items-start gap-space-2 text-caption text-ink cursor-pointer">
          <input
            type="checkbox"
            className="mt-space-1 accent-gold"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-testid="prequal-consent"
          />
          {/* Blank published consent drops the empty span only (D4) — the
              checkbox stays, and finalize still requires it to be ticked. */}
          {hasText(consentLine) && <span>{consentLine}</span>}
        </label>

        {hasText(finePrint) && (
          <p className="text-caption text-ink-faint m-0" data-testid="prequal-fine-print">
            {finePrint}
          </p>
        )}

        {beforeSubmit}

        {error && (
          <p className="text-small text-status-error m-0" role="alert">
            {error}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-space-3">
          <SubmitButton
            type="submit"
            pending={mutation.pending}
            pendingLabel={softDecline ? "Saving…" : "Preparing your documents…"}
            disabled={!consent}
            className="hover:bg-gold-deep hover:text-white-pure"
            data-testid="prequal-finalize"
          >
            FINALIZE →
          </SubmitButton>
        </div>
        <SubmittingHint
          pending={mutation.pending}
          message="Preparing your documents — this can take a moment. Thanks for your patience."
        />
      </form>
    </div>
  );
}
